import { Navigate, useLocation } from "react-router-dom";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { useAuth } from "@/contexts/AuthContext";

type TenantRole = "owner" | "admin" | "member" | "read_only";
type PlatformRole = "root" | "root_admin";
type AllowedRole = TenantRole | PlatformRole;

interface ProtectedRouteProps {
  children: JSX.Element;
  allowedRoles?: AllowedRole[];
}

export function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isVerifyEmailPage = location.pathname === "/verify-email";

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Email verification is a fraud/onboarding gate for tenant users; root and
  // root_admin accounts (Salkantai staff) are provisioned via internal
  // tooling, not the self-serve register flow, so bouncing them to the
  // 6-digit code screen on every login is pure friction.
  const isPlatformRole =
    user?.platformRole === "root" || user?.platformRole === "root_admin";
  if (
    user != null &&
    !isPlatformRole &&
    user.emailVerifiedAt == null &&
    !isVerifyEmailPage
  ) {
    const returnTo = location.pathname + location.search;
    const target =
      returnTo && returnTo !== "/" && returnTo !== "/verify-email"
        ? `/verify-email?returnTo=${encodeURIComponent(returnTo)}`
        : "/verify-email";
    return <Navigate to={target} replace />;
  }

  if (allowedRoles != null && allowedRoles.length > 0 && user != null) {
    const hasRole = allowedRoles.some(
      (r) => r === user.role || r === user.platformRole
    );
    if (!hasRole) {
      if (user.platformRole === "root" || user.platformRole === "root_admin") {
        return <Navigate to="/tenants" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}
